/**
 * Reusable UI components.
 */

import { el } from "../utils/dom";
import { audioManager } from "../../data/audio";

export type ButtonOptions = {
  variant?: "primary" | "secondary" | "danger";
  disabled?: boolean;
  className?: string;
};

export function button(
  text: string,
  onClick: () => void,
  opts: ButtonOptions = {},
): HTMLButtonElement {
  const variant = opts.variant ?? "primary";
  const classNames = [`btn`, `btn-${variant}`];
  if (opts.className) classNames.push(opts.className);
  const btn = el("button", {
    className: classNames.join(" "),
    text,
    onClick: () => {
      void audioManager.playEffect({ type: "ui", sound: "click" });
      onClick();
    },
  }) as HTMLButtonElement;
  if (opts.disabled) {
    btn.disabled = true;
  }
  return btn;
}

export function modal(
  title: string,
  content: HTMLElement | HTMLElement[],
  onClose?: () => void,
): HTMLElement {
  const overlay = el("div", { className: "modal-overlay" });
  const dialog = el("div", { className: "modal-dialog" });
  const header = el("div", { className: "modal-header" });
  header.appendChild(el("h2", { text: title }));
  const closeBtn = el("button", { className: "modal-close", text: "×" });
  closeBtn.addEventListener("click", () => {
    overlay.remove();
    onClose?.();
  });
  header.appendChild(closeBtn);
  dialog.appendChild(header);
  const body = el("div", { className: "modal-body" });
  if (Array.isArray(content)) {
    for (const c of content) body.appendChild(c);
  } else {
    body.appendChild(content);
  }
  dialog.appendChild(body);
  overlay.appendChild(dialog);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) {
      overlay.remove();
      onClose?.();
    }
  });
  return overlay;
}

export function dropdown(
  label: string,
  options: { value: string; label: string }[],
  selectedValue: string,
  onChange: (value: string) => void,
): { container: HTMLElement; select: HTMLSelectElement } {
  const container = el("div", { className: "field" });
  container.appendChild(el("label", { className: "field-label", text: label }));
  const select = el("select", { className: "dropdown" }) as HTMLSelectElement;
  for (const opt of options) {
    const option = el("option", { text: opt.label, attrs: { value: opt.value } });
    select.appendChild(option);
  }
  select.value = selectedValue;
  select.addEventListener("change", () => {
    void audioManager.playEffect({ type: "ui", sound: "click" });
    onChange(select.value);
  });
  container.appendChild(select);
  return { container, select };
}

let toastContainer: HTMLElement | null = null;

function ensureToastContainer(): HTMLElement {
  if (!toastContainer) {
    toastContainer = el("div", { className: "toast-container" });
    document.body.appendChild(toastContainer);
  }
  return toastContainer;
}

export function toast(message: string, type: "info" | "success" | "warning" | "error" = "info", duration = 3000): void {
  void audioManager.playEffect({ type: "ui", sound: type === "error" ? "error" : "toast" });
  const container = ensureToastContainer();
  const item = el("div", { className: `toast toast-${type}`, text: message });
  container.appendChild(item);
  // Force reflow to enable transition
  void item.offsetWidth;
  item.classList.add("toast-visible");
  setTimeout(() => {
    item.classList.remove("toast-visible");
    setTimeout(() => item.remove(), 300);
  }, duration);
}

export function autocomplete(
  label: string,
  options: string[],
  initialValue: string,
  onSelect: (value: string) => void,
  opts: { placeholder?: string } = {},
): { container: HTMLElement; input: HTMLInputElement } {
  const container = el("div", { className: "field autocomplete" });
  container.appendChild(el("label", { className: "field-label", text: label }));
  const input = el("input", {
    className: "autocomplete-input",
    attrs: { type: "text", value: initialValue, placeholder: opts.placeholder ?? "Search..." },
  }) as HTMLInputElement;
  const list = el("ul", { className: "autocomplete-list" });

  let highlightedIndex = -1;
  let filtered: string[] = [];

  function updateList(query: string): void {
    list.innerHTML = "";
    filtered = options.filter((o) => o.toLowerCase().includes(query.toLowerCase())).slice(0, 10);
    highlightedIndex = -1;
    if (filtered.length === 0 || query === "") {
      list.style.display = "none";
      return;
    }
    for (const [i, option] of filtered.entries()) {
      const li = el("li", { className: "autocomplete-item", text: option });
      li.addEventListener("mousedown", () => {
        input.value = option;
        list.style.display = "none";
        onSelect(option);
      });
      li.addEventListener("mouseenter", () => {
        highlightedIndex = i;
        renderHighlight();
      });
      list.appendChild(li);
    }
    list.style.display = "block";
  }

  function renderHighlight(): void {
    for (const [i, child] of Array.from(list.children).entries()) {
      child.classList.toggle("autocomplete-item-highlighted", i === highlightedIndex);
    }
  }

  input.addEventListener("focus", () => updateList(input.value));
  input.addEventListener("input", () => updateList(input.value));
  input.addEventListener("blur", () => {
    setTimeout(() => { list.style.display = "none"; }, 150);
  });
  input.addEventListener("keydown", (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      highlightedIndex = Math.min(highlightedIndex + 1, filtered.length - 1);
      renderHighlight();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      highlightedIndex = Math.max(highlightedIndex - 1, 0);
      renderHighlight();
    } else if (e.key === "Enter") {
      if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
        input.value = filtered[highlightedIndex];
        list.style.display = "none";
        onSelect(filtered[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      list.style.display = "none";
    }
  });

  container.appendChild(input);
  container.appendChild(list);
  return { container, input };
}

export function numberInput(
  label: string,
  initialValue: number,
  onChange: (value: number) => void,
  opts: { min?: number; max?: number; step?: number } = {},
): { container: HTMLElement; input: HTMLInputElement } {
  const container = el("div", { className: "field" });
  container.appendChild(el("label", { className: "field-label", text: label }));
  const input = el("input", {
    className: "number-input",
    attrs: { type: "number", value: String(initialValue), min: String(opts.min ?? 0), max: String(opts.max ?? 999), step: String(opts.step ?? 1) },
  }) as HTMLInputElement;
  input.addEventListener("change", () => {
    const value = Number(input.value);
    if (!Number.isNaN(value)) {
      onChange(value);
    }
  });
  container.appendChild(input);
  return { container, input };
}

export function statSlider(
  label: string,
  initialValue: number,
  onChange: (value: number) => void,
  opts: { min?: number; max?: number } = {},
): { container: HTMLElement; input: HTMLInputElement } {
  const min = opts.min ?? 0;
  const max = opts.max ?? 252;
  const container = el("div", { className: "field" });
  const row = el("div", { className: "stat-slider-row" });
  row.appendChild(el("span", { className: "stat-slider-label", text: label }));
  const input = el("input", {
    className: "stat-slider-input",
    attrs: { type: "range", min: String(min), max: String(max), value: String(initialValue) },
  }) as HTMLInputElement;
  const valueEl = el("span", { className: "stat-slider-value", text: String(initialValue) });
  input.addEventListener("input", () => {
    const value = Number(input.value);
    valueEl.textContent = String(value);
    onChange(value);
  });
  row.appendChild(input);
  row.appendChild(valueEl);
  container.appendChild(row);
  return { container, input };
}
